import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCopy, FaDownload } from 'react-icons/fa';
import { useQuotesStore } from '../quotesStore';
import { quotesService } from '../quotesService';
import ssoService from '../../Auth/ssoService';
import type { QuoteList, QuoteStatus, User } from '../types';
import { QUOTE_STATUS_OPTIONS } from '../constants';
import { Input } from '../../../components/forms/Input';
import { Select } from '../../../components/forms/Select';
import { Badge } from '../../../components/Badge';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { TableLoader } from '../../../components/Table';
import { cn } from '../../../utils/cn';
import { getDownloadFileName } from '../../../utils/download';

interface QuoteListProps {
  onQuoteClick?: (quote: QuoteList) => void;
}

export const QuoteListComponent: React.FC<QuoteListProps> = ({ onQuoteClick }) => {
  const navigate = useNavigate();
  const {
    quotes,
    quotesLoading,
    quotesError,
    quotesPagination,
    quotesFilters,
    fetchQuotes,
    setFilters,
    fetchQuoteDetail,
    currentQuote,
    deleteQuote,
  } = useQuotesStore();

  const [search, setSearch] = useState(quotesFilters.search);
  const [statusFilter, setStatusFilter] = useState<QuoteStatus | ''>((quotesFilters.status ?? '') as QuoteStatus | '');
  const [createdByFilter, setCreatedByFilter] = useState<string>(quotesFilters.created_by ?? '');
  const [createdByUsers, setCreatedByUsers] = useState<User[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; quoteId: number | null; quoteName: string; version: number | null }>({
    isOpen: false,
    quoteId: null,
    quoteName: '',
    version: null,
  });

  const pageSize = 20;
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (isInitialLoad) {
      fetchQuotes({
        search: search || undefined,
        status: statusFilter || undefined,
        created_by: createdByFilter || undefined,
        page: 1,
        page_size: pageSize,
      });
      setIsInitialLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;
    quotesService
      .getCreatedByUsers()
      .then((users) => {
        if (active) setCreatedByUsers(users);
      })
      .catch(() => {
        if (active) setCreatedByUsers([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (isInitialLoad) return;

    fetchQuotes({
      search: search || undefined,
      status: statusFilter || undefined,
      created_by: createdByFilter || undefined,
      page: quotesFilters.page,
      page_size: pageSize,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotesFilters.page, statusFilter, createdByFilter]);

  useEffect(() => {
    if (isInitialLoad) return;
    if (search === quotesFilters.search) return;
    
    const timer = setTimeout(() => {
      setFilters({ search, page: 1 });
      fetchQuotes({
        search: search || undefined,
        status: statusFilter || undefined,
        created_by: createdByFilter || undefined,
        page: 1,
        page_size: pageSize,
      });
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handlePageChange = (newPage: number) => {
    setFilters({ page: newPage });
    fetchQuotes({
      search: search || undefined,
      status: statusFilter || undefined,
      created_by: createdByFilter || undefined,
      page: newPage,
      page_size: pageSize,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.quoteId) return;
    
    try {
      await deleteQuote(deleteConfirm.quoteId);
      setDeleteConfirm({ isOpen: false, quoteId: null, quoteName: '', version: null });
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to delete quote');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, quoteId: null, quoteName: '', version: null });
  };

  const handleQuoteClick = (quote: QuoteList) => {
    if (onQuoteClick) {
      onQuoteClick(quote);
    } else {
      navigate(`/quotes/${quote.id}`);
    }
  };

  const handleCloneClick = async (quote: QuoteList) => {
    try {
      // Fetch full quote detail for cloning directly from service
      const quoteDetail = await quotesService.getQuoteDetail(quote.id);
      // Navigate to create form with prefill data
      navigate('/quotes/new', { state: { prefill: quoteDetail } });
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to load quote for cloning');
    }
  };

  const getStatusVariant = (status: QuoteStatus): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    switch (status) {
      case 'approved':
      case 'closed_won':
        return 'success';
      case 'pricing_review_needed':
        return 'warning';
      case 'expired_outdated_census':
      case 'closed_lost':
        return 'error';
      case 'proposal_submitted':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const formatUser = (user: { first_name: string; last_name: string }): string => {
    return `${user.first_name} ${user.last_name}`;
  };

  const formatCurrency = (value: string | null): string => {
    if (!value) return '-';
    const values = value.split(',').map(v => v.trim());
    return values
      .map(v => {
        const num = parseFloat(v);
        if (isNaN(num)) return v;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(num);
      })
      .join(', ');
  };

  const renderDownloadIconWithBadge = (badge: 'C' | 'A' | 'P', size = 16) => (
    <span className="relative inline-flex items-center justify-center">
      <FaDownload size={size} />
      <span
        className="absolute -top-1 -right-1 rounded-full text-[8px] font-bold leading-none px-1 py-0.5"
        style={{
          backgroundColor: 'var(--color-primary-500)',
          color: 'white',
        }}
      >
        {badge}
      </span>
    </span>
  );

  const handleDownloadCensus = async (quoteId: number) => {
    try {
      let quote = currentQuote?.id === quoteId ? currentQuote : null;
      
      if (!quote) {
        await fetchQuoteDetail(quoteId);
        quote = useQuotesStore.getState().currentQuote;
      }
      
      if (!quote?.census_file) {
        alert('No census file available for this quote');
        return;
      }

      const fileUrl = quote.census_file.startsWith('http://') || quote.census_file.startsWith('https://')
        ? quote.census_file
        : `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}${quote.census_file}`;
      
      const token = ssoService.getAccessToken();
      const response = await fetch(fileUrl, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      
      const blob = await response.blob();
      const filename = getDownloadFileName(
        quote.census_file,
        quote.census_file_name,
        'census_file.xlsx',
        '.xlsx',
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download census file');
    }
  };

  const handleDownloadAnalysisFile = async (quote: QuoteList) => {
    if (!quote.analysis_file || !quote.is_analysis_file_generated) return;

    try {
      const fileUrl = quote.analysis_file.startsWith('http://') || quote.analysis_file.startsWith('https://')
        ? quote.analysis_file
        : `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}${quote.analysis_file}`;

      const token = ssoService.getAccessToken();
      const response = await fetch(fileUrl, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const filename = getDownloadFileName(quote.analysis_file, null, 'analysis_file.xlsx', '.xlsx');

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      if (quote.analysis_file) {
        const fileUrl = quote.analysis_file.startsWith('http://') || quote.analysis_file.startsWith('https://')
          ? quote.analysis_file
          : `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}${quote.analysis_file}`;
        window.open(fileUrl, '_blank');
      }
    }
  };

  const handleDownloadPresentationFile = async (quote: QuoteList) => {
    const isReady =
      Boolean(quote.is_analysis_file_generated) &&
      Boolean(quote.analysis_file) &&
      Boolean(quote.is_power_point_presentation_generated) &&
      Boolean(quote.power_point_presentation_file);

    if (!isReady) return;

    try {
      const { blob, filename } = await quotesService.downloadPowerPointPresentation(quote.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download presentation file');
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            Quotes
          </h1>
        </div>
        <button
          onClick={() => navigate('/quotes/new')}
          className="px-4 py-2 rounded-lg text-white font-medium"
          style={{ backgroundColor: 'var(--color-primary-500)' }}
        >
          Create Quote
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="w-full sm:w-96">
          <Input
            placeholder="Search by quote name or prospect name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
          />
        </div>
        <div className="w-full sm:w-64">
          <Select
            options={[
              { value: '', label: 'All Statuses' },
              ...QUOTE_STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
            ]}
            value={statusFilter}
            onChange={(e) => {
              const newStatus = e.target.value as QuoteStatus | '';
              setStatusFilter(newStatus);
              setFilters({ status: newStatus || null, page: 1 });
            }}
            fullWidth
          />
        </div>
        <div className="w-full sm:w-64">
          <Select
            options={[
              { value: '', label: 'All Users' },
              ...createdByUsers.map((user) => ({
                value: String(user.id),
                label: `${user.first_name} ${user.last_name}`.trim() || user.email,
              })),
            ]}
            value={createdByFilter}
            onChange={(e) => {
              const newCreatedBy = e.target.value;
              setCreatedByFilter(newCreatedBy);
              setFilters({ created_by: newCreatedBy || null, page: 1 });
            }}
            fullWidth
          />
        </div>
      </div>

      <div
        className="rounded-lg border overflow-hidden"
        style={{ backgroundColor: 'var(--color-background-paper)', borderColor: 'var(--color-border-light)' }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border-light)' }}>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Prospect Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Quote Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Version
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Benefits
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Weekly Cap
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Length of Leave (P)
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Length of Leave (S)
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Length of Service
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Employees Accepted
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Employees Ignored
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Status
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Last Modified
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Created By
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {quotesLoading && quotes.length === 0 ? (
                <TableLoader colSpan={14} message="Loading quotes..." />
              ) : quotesError && quotes.length === 0 ? (
                <tr key="error">
                  <td colSpan={14} className="px-4 py-8 text-center" style={{ color: 'var(--color-error-500)' }}>
                    Error: {quotesError}
                  </td>
                </tr>
              ) : quotes.length === 0 && !quotesLoading ? (
                <tr key="empty">
                  <td colSpan={14} className="px-4 py-8 text-center" style={{ color: 'var(--color-text-secondary)' }}>
                    No quotes found
                  </td>
                </tr>
              ) : (
                quotes.map((quote) => (
                    <tr
                      key={quote.id}
                      style={{ borderBottom: '1px solid var(--color-border-light)' }}
                    >
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => handleQuoteClick(quote)}
                          className="text-left font-medium hover:underline"
                          style={{ color: 'var(--color-primary-500)' }}
                        >
                          {quote.prospect_name}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-primary)' }}>
                        {quote.quote_name}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {quote.version}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {quote.benefit?.join(', ') || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {formatCurrency(quote.weekly_cap)}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {quote.length_of_leave?.join(', ') || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {quote.length_of_leave_weeks_sec?.join(', ') || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {quote.length_of_service?.join(', ') || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {quote.employee_count ?? 0}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {quote.employees_ignored_count ?? 0}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={getStatusVariant(quote.status)} size="sm">
                          {QUOTE_STATUS_OPTIONS.find((opt) => opt.value === quote.status)?.label || quote.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {formatDate(quote.modified)}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {formatUser(quote.created_by)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleCloneClick(quote)}
                            className="p-2 rounded hover:bg-[var(--color-hover-background)]"
                            title="Clone"
                            style={{ color: 'var(--color-text-secondary)' }}
                          >
                            <FaCopy size={16} />
                          </button>
                          <button
                            onClick={() => handleDownloadCensus(quote.id)}
                            className="p-2 rounded hover:bg-[var(--color-hover-background)]"
                            title="Download Census"
                            style={{ color: 'var(--color-text-secondary)' }}
                          >
                            {renderDownloadIconWithBadge('C')}
                          </button>
                          <button
                            onClick={() => quote.is_analysis_file_generated && quote.analysis_file ? handleDownloadAnalysisFile(quote) : undefined}
                            className="p-2 rounded hover:bg-[var(--color-hover-background)]"
                            title={quote.is_analysis_file_generated ? 'Download Analysis File' : 'Analysis file not yet generated'}
                            style={{
                              color: 'var(--color-text-secondary)',
                              opacity: quote.is_analysis_file_generated && quote.analysis_file ? 1 : 0.4,
                              cursor: quote.is_analysis_file_generated && quote.analysis_file ? 'pointer' : 'default',
                            }}
                            disabled={!quote.is_analysis_file_generated || !quote.analysis_file}
                          >
                            {renderDownloadIconWithBadge('A')}
                          </button>
                          <button
                            onClick={() =>
                              quote.is_analysis_file_generated &&
                              quote.analysis_file &&
                              quote.is_power_point_presentation_generated &&
                              quote.power_point_presentation_file
                                ? handleDownloadPresentationFile(quote)
                                : undefined
                            }
                            className="p-2 rounded hover:bg-[var(--color-hover-background)]"
                            title={
                              quote.is_analysis_file_generated &&
                              quote.analysis_file &&
                              quote.is_power_point_presentation_generated &&
                              quote.power_point_presentation_file
                                ? 'Download Presentation'
                                : 'Presentation not yet generated'
                            }
                            style={{
                              color: 'var(--color-text-secondary)',
                              opacity:
                                quote.is_analysis_file_generated &&
                                quote.analysis_file &&
                                quote.is_power_point_presentation_generated &&
                                quote.power_point_presentation_file
                                  ? 1
                                  : 0.4,
                              cursor:
                                quote.is_analysis_file_generated &&
                                quote.analysis_file &&
                                quote.is_power_point_presentation_generated &&
                                quote.power_point_presentation_file
                                  ? 'pointer'
                                  : 'default',
                            }}
                            disabled={
                              !quote.is_analysis_file_generated ||
                              !quote.analysis_file ||
                              !quote.is_power_point_presentation_generated ||
                              !quote.power_point_presentation_file
                            }
                          >
                            {renderDownloadIconWithBadge('P')}
                          </button>
                          {/* Delete button hidden
                          <button
                            onClick={() => handleDeleteClick(quote)}
                            className="p-2 rounded hover:bg-[var(--color-hover-background)]"
                            title="Delete"
                            style={{ color: 'var(--color-error-500)' }}
                          >
                            <FaTrash size={16} />
                          </button>
                          */}
                        </div>
                      </td>
                    </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Quote"
        message={`Are you sure you want to delete "${deleteConfirm.quoteName}" (v${deleteConfirm.version})? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {quotesPagination.count > pageSize && (
        <div className="flex items-center justify-between">
          <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Showing {quotes.length} of {quotesPagination.count} quotes
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(Math.max(1, quotesFilters.page - 1))}
              disabled={!quotesPagination.previous}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium',
                !quotesPagination.previous && 'opacity-50 cursor-not-allowed'
              )}
              style={{
                backgroundColor: quotesPagination.previous ? 'var(--color-primary-500)' : 'var(--color-neutral-300)',
                color: 'white',
              }}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(quotesFilters.page + 1)}
              disabled={!quotesPagination.next}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium',
                !quotesPagination.next && 'opacity-50 cursor-not-allowed'
              )}
              style={{
                backgroundColor: quotesPagination.next ? 'var(--color-primary-500)' : 'var(--color-neutral-300)',
                color: 'white',
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

